import Vue from 'vue'
import { mapActions, mapState } from 'vuex'
import { InvalidDataError, NoDataError } from '@/classes/errors'
import Status from '@/classes/http/Status'
import bugsnagClient from '@/utility/bugsnag'
import FormData from '@/classes/form/FormData'
import AuthUser from '@/classes/auth/AuthUser'
import { findItem } from '@/store/utility_functions'
import TableFilter from '@/classes/case/TableFilter'
import { extractErrorMessages } from '@/utility/errorMessages'

/*
  Adds an ation when clicking the Enter key
*/
export let onKeyEnterMixin = {
  methods: {
    onEnter (e) {
      if (e.keyCode === 13) {
        this.$refs.enterButton.submit()
      }
    }
  },
  created () {
    window.addEventListener('keydown', this.onEnter)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onEnter)
  }
}

/*
  Handles errors for requests
 */
export let errorHandler = {
  methods: {
    ...mapActions([
      'errorMessage',
      'logoutUser'
    ]),
    handleError (err) {
      if (err.response && err.response.status === Status.FORBIDDEN) {
        this.logoutUser()
        // bugsnagClient.notify(err)
      } else if (err.response) {
        console.log('RUNTIME ERROR:', err)
        let messages = extractErrorMessages(err.response.data)
        if (!messages.length) {
          messages = ['Something went wrong. Support has been notified.']
        }
        for (let text of messages) {
          this.errorMessage(text)
        }
        bugsnagClient.notify(err)
      } else {
        this.errorMessage('Handle runtime error here: ' + err)
        bugsnagClient.notify(err)
        throw err
      }
    }
  }
}

/*
  Handles errors for seriline.
 */
export let serilineErrorHandler = {
  methods: {
    ...mapActions([
      'errorMessage'
    ]),
    handleSerilineError (err) {
      if (typeof err.response.data === 'object' && !!err.response.data.detail) {
        this.errorMessage(err.response.data.detail)
      } else if (typeof err.response.data === 'object' && !!err.response.data.message) {
        this.errorMessage(err.response.data.message)
      } else {
        this.errorMessage('There was a problem sending the card orders. Support has been notified. ')
      }
      bugsnagClient.notify(err)
    }
  }
}

/*
  Used to load items in the state
*/
export let loadItemsMixin = Vue.util.mergeOptions(errorHandler, {
  data () {
    return {
      mixin: {
        init: null,
        total_items: 0,
        loaded: false,
        filters: null,
        certificateDateFilters: {
          date_issued: {
            type: TableFilter.FILTER_TYPE_DATE_RANGE,
            limit_start: 'issued_gte',
            limit_end: 'issued_lte'
          },
          date_expired: {
            type: TableFilter.FILTER_TYPE_DATE_RANGE,
            limit_start: 'expiry_gte',
            limit_end: 'expiry_lte'
          }
        },
        actionMethod: {},
        actionParams: {},
        loadItems: this.$_loadItemsMixin_loadItems,
        computedActionParams: () => {}
      }
    }
  },
  methods: {
    ...mapActions([
      'errorMessage'
    ]),
    $_loadItemsMixin_loadItems (requestData) {
      let itemParams = {}
      Object.assign(itemParams, this.mixin.actionParams)
      Object.assign(itemParams, requestData)
      return this.mixin.actionMethod(itemParams).then((items) => {
          return items
        }).catch((err) => {
          this.handleError(err)
        })
    },
    loaditems_load () {
      Object.assign(this.mixin.actionParams, this.mixin.computedActionParams())
      this.mixin.init = this.$_loadItemsMixin_loadItems(this.mixin.actionParams).then((items) => {
        this.mixin.total_items = items ? items.count : 0
        this.mixin.filters = items ? (items.filters ? items.filters : {}) : {}
        this.mixin.loaded = true
      })
    }
  },
  created () {
    this.loaditems_load()
  }
})

/*
  Handles the submission of forms
 */
let VueScrollTo = require('vue-scrollto')

export let formHandlerMixin = Vue.util.mergeOptions(errorHandler, {
  data () {
    return {
      mixin: {
        callbackData: {},
        error: ''
      }
    }
  },
  methods: {
    ...mapActions([
      'logoutUser',
      'errorMessage'
    ]),
    getFormDataForDescriptor (formDescriptor, formDatas) {
      return formDescriptor.getFormData(formDatas || this.formDatas)
    },
    serializeForm (requestData) {
      let serializedFormAndFileData = FormData.serializeFormDatas(requestData.formDatas)
      let serializedData = serializedFormAndFileData.form_data.filter(formData => formData.data_objects && formData.data_objects.length > 0)
      if (serializedData.length > 0) {
        return this.submitRequest({ data: requestData, serializedData: serializedFormAndFileData })
      }
      throw new NoDataError('No data supplied')
    },
    validateForm (requestData) {
      return new Promise((resolve, reject) => {
        if (!FormData.validateFormDatas(requestData.formDatas)) {
          throw new InvalidDataError('Invalid data')
        }
        resolve()
      }).then((response) => {
        return this.serializeForm(requestData)
      }).catch((err) => {
        throw err
      })
    },
    submitForm (requestData, btn, callback) {
      return this.validateForm(requestData).then((response) => {
        btn && btn.enable()
        callback && callback(response, this.mixin.callbackData)
        return response
      }).catch((err) => {
        btn && btn.enable()
        if (this.showErrorMessage(err)) {
          return
        }
        this.handleError(err)
      })
    },
    showErrorMessage (err) {
      if (err.name === InvalidDataError.NAME) {
        this.scrollToFirstError()
        return true
      }
      if (err.name === NoDataError.NAME) {
        this.errorMessage('No information added')
        return true
      }
      if (err.status === Status.INTERNAL_SERVER_ERROR) {
        this.errorMessage('Could not communicate with server. Code: ' + err.status)
        return true
      }
      if (err.status === Status.FORBIDDEN) {
        this.logoutUser().catch((err) => {
          this.handleError(err)
        })
        return true
      }
      if (err.status === Status.SERVICE_UNAVAILABLE) {
        if (err.data && err.data.detail) {
          this.errorMessage(err.data.detail + ' Code: ' + err.status)
          return true
        }
        this.errorMessage('Service unavailable. Code: ' + err.status)
        return true
      }
      if (err.data) {
        this.handleResponseInvalidSyntax(err.data)
        this.scrollToFirstError()
        return true
      }
      return err.response ? this.showErrorMessage(err.response) : false
    },
    handleResponseInvalidSyntax (responseData) {
      if (Array.isArray(responseData)) {
        this.formDatas.forEach(formData => {
          formData.handleInvalidResponse(responseData)
        })
        responseData.forEach(data => {
          if (data.non_field_errors) {
            this.errorMessage(data.non_field_errors.join(', '))
          }
        })
      }
      if (responseData.non_field_errors) {
        this.errorMessage(responseData.non_field_errors.join(', '))
      }
      if (responseData.message) {
        this.errorMessage(responseData.message)
      }
    },
    scrollToFirstError () {
      this.$nextTick(() => {
        let invalidElement = document.getElementsByClassName('invalid')[0]
        if (invalidElement) {
          VueScrollTo.scrollTo(invalidElement)
        }
      })
    }
  }
})

/*
  Used for pages with two floating divisions
*/
export let setPageHeightMixin = {
  data () {
    return {
      scrolled: false
    }
  },
  computed: {
    hasShadow () {
      return { shadow: this.scrolled }
    }
  },
  methods: {
    setPageHeight () {
      let headerHeight = document.getElementsByTagName('header')[0].offsetHeight
      let contentHeight = window.innerHeight - headerHeight
      let paddingHeight = Math.ceil(3 * parseFloat(getComputedStyle(document.documentElement).fontSize))
      let staticHeight = contentHeight - paddingHeight * 2
      document.getElementsByClassName('main-container')[0].style.height = contentHeight + 'px'
      document.getElementsByClassName('main-view')[0].style.height = staticHeight + 'px'
      document.getElementsByClassName('main-view')[0].style.minHeight = staticHeight + 'px'
    },
    resetPageHeight () {
      document.getElementsByClassName('main-container')[0].style.height = 'auto'
    },
    scrollReviewForm (event) {
      this.scrolled = event.target.scrollTop > 0
    }
  },
  mounted () {
    this.setPageHeight()
    window.addEventListener('resize', this.setPageHeight)
    this.$refs.form.addEventListener('scroll', this.scrollReviewForm)
  },
  beforeDestroy () {
    this.$refs.form.removeEventListener('scroll', this.scrollReviewForm)
    window.removeEventListener('resize', this.setPageHeight)
    this.resetPageHeight()
  }
}

/*
  Flex bug fix only for IE11
*/
export let setContainedHeightMixin = {
  data () {
    return {
      isIE11: !!window.MSInputMethodContext && !!document.documentMode,
      height: 0
    }
  },
  methods: {
    setContainedHeight () {
      if (this.isIE11) {
        let containedElement = document.getElementsByClassName('contained')[0]
        this.setContentHeight()
        containedElement.style.flex = 'none'
        containedElement.style.height = this.height + 'px'
      }
    },
    setContentHeight () {
      let contentElement = document.getElementsByTagName('section')[0]
      let headerHeight = document.getElementsByTagName('header')[0].offsetHeight
      let windowHeight = window.innerHeight - headerHeight
      let height = contentElement && contentElement.offsetHeight + headerHeight
      height = height > windowHeight ? height : windowHeight
      this.height = (height - headerHeight) > this.height ? height : this.height
    }
  },
  mounted () {
    this.setContainedHeight()
  },
  updated () {
    this.setContainedHeight()
  }
}

/*
  Makes sure that an item is updated with its latest version from the backend
 */
export let itemRefresher = Vue.util.mergeOptions(errorHandler, {
  data () {
    return {
      setAction: null
    }
  },
  methods: {
    runCallbacks (callbacks = []) {
      for (let callback of callbacks) {
        callback()
      }
    },
    /*
      calls to this function should not be stacked within each other (added to the callbacks),
      because when refreshing the calls to setAction will grow exponentially
    */
    setOrRefreshItem (item, items, itemId, callbacks) {
      let foundItem = findItem(items, itemId)
      if (foundItem) {
        this[item] = foundItem
      }
      return this.setAction(itemId).then((result) => {
        this[item] = result
        this.runCallbacks(callbacks)
      }).catch((err) => {
        this.handleError(err)
      })
    }
  }
})

/*
  Switches to customer
 */
export let switchToCustomerMixin = {
  computed: {
    ...mapState({
      authUser: state => state.authModule.authUser
    })
  },
  methods: {
    ...mapActions([
      'switchProfile',
      'logoutUser'
    ]),
    $_switchToCustomerMixin_logout () {
      this.logoutUser().catch((err) => {
        this.handleError(err)
      })
    },
    $_switchToCustomerMixin_switch (userId, path) {
      let routeData = {
        userId: userId,
        profileType: this.authUser.profile.getSwitchType(),
        path: path
      }
      if (AuthUser.isLoggedIn()) {
        this.switchProfile(routeData).catch((err) => {
          this.handleError(err)
        })
      } else {
        this.$_switchToCustomerMixin_logout()
      }
    }
  }
}
/*
 Generic switch to profile
 */
export let switchToProfileMixin = {
  computed: {
    ...mapState({
      authUser: state => state.authModule.authUser
    })
  },
  methods: {
    ...mapActions([
      'switchProfile',
      'logoutUser'
    ]),
    $_switchToProfileMixin_logout () {
      this.logoutUser().catch((err) => {
        this.handleError(err)
      })
    },
    $_switchToProfileMixin_switch (userId, path) {
      let routeData = {
        userId: userId,
        profileType: this.authUser.profile.getSwitchType(),
        path: path
      }
      if (AuthUser.isLoggedIn()) {
        this.switchProfile(routeData).catch((err) => {
          this.handleError(err)
        })
      } else {
        this.$_switchToProfileMixin_logout()
      }
    }
  }
}
